import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  CircularProgress,
  TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import { handleConfirmMultiClassify } from "../utils/Handler";
import { updateIsPredictedMetadata } from "../../../../services/StorageServices";
// Category labels
const CATEGORIES = {
  Leadership_and_Governance: "Leadership and Governance",
  Quality_Assurance: "Quality Assurance",
  Resource_Management: "Resource Management",
  Teaching_and_Learning: "Teaching and Learning",
  Student_Services: "Student Services",
  External_Relations: "External Relations",
  Research: "Research",
};

function MultiPageClassificationResultDialog({
  open,
  result,
  files,
  userId,
  selectedFolder,
  onClose,
  updateAccreditation,
  selectedFile,
  departmentId,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [pageCategories, setPageCategories] = useState({});
  const [lowConfidenceItems, setLowConfidenceItems] = useState([]);
  const [selectedPageNumber, setSelectedPageNumber] = useState(1);
  const [editLogs, setEditLogs] = useState([]);

  // Seed state from API
  useEffect(() => {
    if (result?.pages) {
      const cats = {};
      const lows = [];
      result.pages.forEach((page) => {
        // Use predicted_category from new format or fallback
        const label =
          page.predicted_category || page.predictions?.[0]?.category || "";
        cats[page.pageName] = label;

        // Generate random confidence between 0.75 and 1.0 for demo
        const randomConfidence = 0.75 + Math.random() * 0.25;
        page._demoConfidence = randomConfidence;

        if (randomConfidence < 0.7)
          lows.push({ pageName: page.pageName, probability: randomConfidence });
      });
      setPageCategories(cats);
      setLowConfidenceItems(lows.sort((a, b) => a.probability - b.probability));
      // default to first page
      const firstMatch = result.pages[0]?.pageName.match(/_page(\d+)/);
      setSelectedPageNumber(firstMatch ? parseInt(firstMatch[1], 10) : 1);
    }
  }, [result]);

  if (!result) return null;

  const file = selectedFile || files?.[0] || null;
  const fileUrl = file?.downloadURL;

  const handleCategoryChange = (pageName, label) => {
    setPageCategories((prev) => {
      const oldCategory = prev[pageName];
      if (oldCategory === label) return prev; // No change
      return { ...prev, [pageName]: label };
    });

    setEditLogs((logs) => {
      // Prevent duplicate log for the same change at the same time
      const lastLog = logs[logs.length - 1];
      const oldCategory = pageCategories[pageName];
      if (
        lastLog &&
        lastLog.pageName === pageName &&
        lastLog.oldCategory === oldCategory &&
        lastLog.newCategory === label
      ) {
        return logs;
      }
      return [
        ...logs,
        {
          pageName,
          pageNumber: parseInt(pageName.match(/_page(\d+)/)?.[1] || "0", 10),
          oldCategory,
          newCategory: label,
          timestamp: new Date().toLocaleTimeString(),
        },
      ];
    });

    setLowConfidenceItems((items) =>
      items.filter((item) => item.pageName !== pageName)
    );
  };

  const handleConfirm = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    console.log("confirm button is clicked...");

    //For uploading re-assigned files into storage Admin/Training_files
    try {
      // Import the upload service dynamically
      const { uploadReassignedPageImage } = await import(
        "../../../../services/ReassignedFileUploadService"
      );

      // Identify pages with changed categories
      const changedPages = Object.entries(pageCategories).filter(
        ([pageName, newCategory]) => {
          const originalCategory =
            result.pages.find((p) => p.pageName === pageName)
              ?.predicted_category || "";
          return newCategory !== originalCategory;
        }
      );

      // Base filename from selectedFile
      const baseFilename = selectedFile?.name || "";

      // Upload each changed page image
      for (const [pageName, newCategory] of changedPages) {
        // Derive page image filename from pageName, e.g. pageName might be "document_page1.jpg"
        // Assuming pageName corresponds to the image filename directly
        const pageImageFilename = pageName;
        try {
          await uploadReassignedPageImage(
            userId,
            baseFilename,
            pageImageFilename,
            newCategory,
            departmentId
          );
          console.log("Uploading to Admin/Training_files.... [SUCCESS]");
        } catch (uploadErr) {
          console.error(
            `Failed to upload page image ${pageImageFilename}:`,
            uploadErr
          );
          setError(
            `Failed to upload page image ${pageImageFilename}: ${uploadErr.message}`
          );
        }
      }

      // For generating document summary and compiling the classification report to the original file
      await handleConfirmMultiClassify(
        pageCategories,
        result,
        selectedFile,
        userId,
        selectedFolder,
        setError,
        setLoading,
        setSuccess
      );

      console.log("setting isPredicted for MultiPage Document");
      await updateIsPredictedMetadata(selectedFile.storagePath, true);
      //update the accreditation_folder before closing the dialog.
      console.log("updating Accreditation....");

      updateAccreditation();
      onClose();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // sort pages by number suffix
  const sortedPages = [...result.pages].sort((a, b) => {
    const aN = parseInt(a.pageName.match(/_page(\d+)/)?.[1] || "0", 10);
    const bN = parseInt(b.pageName.match(/_page(\d+)/)?.[1] || "0", 10);
    return aN - bN;
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "90vw",
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
      >
        MULTI-PAGE DOCUMENT CLASSIFICATION RESULT
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Preview pane */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              position: "relative",
            }}
          >
            <Box
              sx={{
                height: "55vh",
                p: 1,
                bgcolor: "#f5f5f5",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              {fileUrl ? (
                <Box
                  component="object"
                  key={`${fileUrl}#page=${selectedPageNumber}`}
                  data={`${fileUrl}#page=${selectedPageNumber}`}
                  type="application/pdf"
                  sx={{ width: "100%", height: "100%" }}
                />
              ) : (
                <FileIcon sx={{ fontSize: 100, color: "primary.main" }} />
              )}
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2">File Details</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                Name: {file?.name || "N/A"}
              </Typography>
              <Typography variant="body2">
                Size: {file ? (file.size / 1024).toFixed(2) : "N/A"} KB
              </Typography>
              <Typography variant="body2">
                Type: {file?.type || "N/A"}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 2, height: "25vh", overflowY: "auto" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Summary
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
              >
                {result.summary}
              </Typography>
            </Paper>
          </Grid>

          {/* Right column: low confidence table & categories */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Low Confidence Pages
              </Typography>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Page #</TableCell>
                      <TableCell>Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lowConfidenceItems.length > 0 ? (
                      lowConfidenceItems.map(({ pageName, probability }) => {
                        const num = parseInt(
                          pageName.match(/_page(\d+)/)?.[1] || "0",
                          10
                        );
                        return (
                          <TableRow
                            key={pageName}
                            hover
                            selected={selectedPageNumber === num}
                            onClick={() => setSelectedPageNumber(num)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>{num}</TableCell>
                            <TableCell>
                              {(probability * 100).toFixed(2)}%
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                          No low confidence pages
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ p: 2, height: "calc(100% - 260px)", overflowY: "auto" }}
            >
              <Typography variant="subtitle1" gutterBottom>
                Adjust Categories
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Page #</TableCell>
                      <TableCell>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedPages.map((page) => {
                      const num = parseInt(
                        page.pageName.match(/_page(\d+)/)?.[1] || "0",
                        10
                      );
                      const label = pageCategories[page.pageName] || "";
                      return (
                        <TableRow key={page.pageName}>
                          <TableCell>{num}</TableCell>
                          <TableCell>
                            <Select
                              size="small"
                              fullWidth
                              value={label}
                              onChange={(e) =>
                                handleCategoryChange(
                                  page.pageName,
                                  e.target.value
                                )
                              }
                            >
                              {Object.values(CATEGORIES).map((lbl) => (
                                <MenuItem key={lbl} value={lbl}>
                                  {lbl}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Edit Logs Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Edit Logs
                </Typography>
                {editLogs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No edits made yet.
                  </Typography>
                ) : (
                  <TableContainer
                    sx={{
                      maxHeight: 220,
                      bgcolor: "#e6ffe6",
                      borderRadius: 1,
                      boxShadow: 1,
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Page #</TableCell>
                          <TableCell>Old Category</TableCell>
                          <TableCell>New Category</TableCell>
                          <TableCell>Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editLogs.map((log, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              bgcolor: "#e6ffe6",
                            }}
                          >
                            <TableCell>{log.pageNumber}</TableCell>
                            <TableCell>{log.oldCategory}</TableCell>
                            <TableCell>{log.newCategory}</TableCell>
                            <TableCell>{log.timestamp}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", px: 3, py: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={handleConfirm}
          sx={{ minWidth: 200 }}
        >
          Confirm Choices
        </Button>
        <Button variant="outlined" onClick={onClose} sx={{ minWidth: 200 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MultiPageClassificationResultDialog;
